// src/services/telemetry.js
// Telemetría de producto (SDK ONEST SmartLogistics). El SDK se carga en
// diferido y todo es tolerante a fallos: si no está configurado o el host no
// responde, la aplicación sigue igual y las llamadas no hacen nada.
import { sesion, normalizaRol } from "./cx.service";

const SDK_URL = process.env.VUE_APP_TELEMETRY_SDK_URL || "";
const COLLECTOR = process.env.VUE_APP_TELEMETRY_COLLECTOR || "";
const APP_ID = process.env.VUE_APP_TELEMETRY_APP_ID || "portal-cx";

const ESPERA_SDK_MS = 8000;
const MAX_COLA = 50;
const CLAVE_PENDIENTES = "tm_pendientes";
const TIPOS_COLLECTOR = ["session_start", "session_end", "page_view", "error", "custom"];

let _carga = null;
let _listo = false;
let _apagado = false;
let _cola = [];
const _avisados = {};

function avisa(clave, mensaje) {
  if (_avisados[clave]) return;
  _avisados[clave] = true;
  console.warn("[telemetria] " + mensaje);
}

const esUrl = (v) => /^https?:\/\/[^<>\s]+$/i.test(v);
// El script del SDK también puede servirse desde el propio sitio (copia en public/sdk/).
const esRutaLocal = (v) => /^\.?\/?[\w-][\w\-./]*\.js$/.test(v);

function cargaScript(url) {
  return new Promise((resolve, reject) => {
    if (window.Telemetry) return resolve();
    const s = document.createElement("script");
    const reloj = setTimeout(() => reject(new Error("sin respuesta")), ESPERA_SDK_MS);
    s.src = url;
    s.async = true;
    s.onload = () => {
      clearTimeout(reloj);
      resolve();
    };
    s.onerror = () => {
      clearTimeout(reloj);
      reject(new Error("no se pudo descargar"));
    };
    document.head.appendChild(s);
  });
}

function ejecuta(metodo, args) {
  try {
    const t = window.Telemetry;
    if (!t || typeof t[metodo] !== "function") {
      avisa(metodo, "window.Telemetry." + metodo + " no está disponible.");
      return;
    }
    t[metodo].apply(t, args);
  } catch (e) {
    avisa("error_" + metodo, metodo + " falló: " + (e && e.message));
  }
}

// Lo que se llama antes de que el SDK termine de cargar espera en cola.
function llama(metodo, args) {
  if (_apagado) return;
  if (_listo) return ejecuta(metodo, args);
  if (_cola.length < MAX_COLA) _cola.push([metodo, args]);
}

/**
 * Carga el SDK e inicializa la telemetría. Idempotente: una vez por carga de página.
 * @returns {Promise<boolean>}  true si quedó activa
 */
export function init() {
  if (_carga) return _carga;

  if (!(esUrl(SDK_URL) || esRutaLocal(SDK_URL)) || !esUrl(COLLECTOR)) {
    _apagado = true;
    avisa("config", "sin configurar (VUE_APP_TELEMETRY_SDK_URL / VUE_APP_TELEMETRY_COLLECTOR); desactivada.");
    _carga = Promise.resolve(false);
    return _carga;
  }

  _carga = cargaScript(SDK_URL)
    .then(() => {
      if (!window.Telemetry || typeof window.Telemetry.init !== "function") {
        throw new Error("el script no expuso window.Telemetry");
      }
      window.Telemetry.init({
        appId: APP_ID,
        endpoint: COLLECTOR,
        batchSize: 10,
        flushIntervalMs: 5000,
        // identifySesion() puede tardar (catálogo en ORDS): sin esta espera los
        // eventos iniciales saldrían anónimos (USR-XXXX) en el dashboard.
        identifyTimeoutMs: 10000,
      });
      _listo = true;
      const pendientes = _cola;
      _cola = [];
      pendientes.forEach((p) => ejecuta(p[0], p[1]));
      return true;
    })
    .catch((e) => {
      _apagado = true;
      _cola = [];
      avisa("carga", "SDK no disponible (" + (e && e.message) + "); desactivada.");
      return false;
    });
  return _carga;
}

/**
 * Asocia los eventos a un usuario. El SDK lo seudonimiza del lado servidor.
 * @param {number|string} id  identificador interno del usuario
 * @param {string} rol
 * @param {string} depto
 */
export function identify(id, rol, depto) {
  if (id === null || id === undefined || id === "") return;
  llama("identify", [String(id), rol || "", depto || ""]);
}

/**
 * Registra un evento de negocio. No incluir datos personales en props.
 * @param {string} evento
 * @param {object} [props]
 */
export function track(evento, props) {
  if (!evento) return;
  // El SDK manda el nombre tal cual como event_type y el collector rechaza el
  // lote completo (422) si no es uno de sus tipos. Los eventos de negocio van
  // como "custom" con su nombre en `action`, igual que los clics del SDK.
  if (TIPOS_COLLECTOR.indexOf(evento) === -1) {
    llama("track", ["custom", Object.assign({ action: evento }, props)]);
    return;
  }
  llama("track", [evento, props || {}]);
}

/**
 * Identifica al usuario de la sesión actual: ID_COLABORADOR, rol y departamento.
 * Sin colaborador resuelto no identifica: no se cae al correo ni al nombre.
 * @returns {Promise<boolean>}  true si se identificó
 */
export async function identifySesion() {
  try {
    const s = await sesion();
    if (s.idColaborador == null) return false;
    let depto = "";
    try {
      depto = localStorage.getItem("departamento") || "";
    } catch (e) {
      depto = "";
    }
    identify(s.idColaborador, normalizaRol(s.rol), depto);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Deja un evento para la siguiente carga de página. Sirve cuando justo después
 * viene un reload (login), que descartaría el lote aún no enviado.
 * @param {string} evento
 * @param {object} [props]
 */
export function trackTrasRecarga(evento, props) {
  try {
    const lista = JSON.parse(sessionStorage.getItem(CLAVE_PENDIENTES) || "[]");
    lista.push([evento, props || {}]);
    sessionStorage.setItem(CLAVE_PENDIENTES, JSON.stringify(lista.slice(-MAX_COLA)));
  } catch (e) {
    track(evento, props);
  }
}

/** Envía los eventos que dejó trackTrasRecarga en la carga anterior. */
export function enviaPendientes() {
  try {
    const lista = JSON.parse(sessionStorage.getItem(CLAVE_PENDIENTES) || "[]");
    sessionStorage.removeItem(CLAVE_PENDIENTES);
    lista.forEach((p) => track(p[0], p[1]));
  } catch (e) {
    avisa("pendientes", "no se pudieron leer los eventos pendientes.");
  }
}

export default { init, identify, track, identifySesion, trackTrasRecarga, enviaPendientes };
